/**
 * High-quality separable Lanczos-3 resampling on ImageData.
 *
 * Canvas `drawImage` downscaling is a box filter in most engines and
 * visibly softens holographic texture. Lanczos preserves the micro-contrast
 * that makes foil and refractor patterns read correctly at thumbnail sizes.
 *
 * For very large reductions we halve with the GPU-backed canvas path first
 * (cheap, artefact-free at exactly 2x) and only run Lanczos on the last leg.
 */

import { ctx2d, makeCanvas, type AnyCanvas } from "./canvas";

const A = 3; // Lanczos kernel radius

function lanczos(x: number): number {
  if (x === 0) return 1;
  const ax = Math.abs(x);
  if (ax >= A) return 0;
  const px = Math.PI * x;
  return (A * Math.sin(px) * Math.sin(px / A)) / (px * px);
}

type Weights = { start: Int32Array; count: Int32Array; w: Float32Array; max: number };

function buildWeights(srcLen: number, dstLen: number): Weights {
  const scale = dstLen / srcLen;
  const support = scale < 1 ? A / scale : A;
  const max = Math.ceil(support * 2) + 2;
  const start = new Int32Array(dstLen);
  const count = new Int32Array(dstLen);
  const w = new Float32Array(dstLen * max);

  for (let i = 0; i < dstLen; i++) {
    const centre = (i + 0.5) / scale - 0.5;
    const lo = Math.max(0, Math.ceil(centre - support));
    const hi = Math.min(srcLen - 1, Math.floor(centre + support));
    let sum = 0;
    let n = 0;
    for (let j = lo; j <= hi && n < max; j++) {
      const t = scale < 1 ? (j - centre) * scale : j - centre;
      const weight = lanczos(t);
      w[i * max + n] = weight;
      sum += weight;
      n++;
    }
    if (sum !== 0) {
      for (let k = 0; k < n; k++) w[i * max + k] /= sum;
    }
    start[i] = lo;
    count[i] = n;
  }
  return { start, count, w, max };
}

/** Resample ImageData to an exact target size with Lanczos-3. */
export function lanczosResize(src: ImageData, dw: number, dh: number): ImageData {
  const sw = src.width;
  const sh = src.height;
  if (sw === dw && sh === dh) return src;

  const horiz = buildWeights(sw, dw);
  const tmp = new Float32Array(dw * sh * 4);

  for (let y = 0; y < sh; y++) {
    const rowOff = y * sw * 4;
    for (let x = 0; x < dw; x++) {
      const s = horiz.start[x];
      const n = horiz.count[x];
      let r = 0, g = 0, b = 0, a = 0;
      for (let k = 0; k < n; k++) {
        const weight = horiz.w[x * horiz.max + k];
        const i = rowOff + (s + k) * 4;
        r += src.data[i] * weight;
        g += src.data[i + 1] * weight;
        b += src.data[i + 2] * weight;
        a += src.data[i + 3] * weight;
      }
      const o = (y * dw + x) * 4;
      tmp[o] = r; tmp[o + 1] = g; tmp[o + 2] = b; tmp[o + 3] = a;
    }
  }

  const vert = buildWeights(sh, dh);
  const out = new ImageData(dw, dh);
  for (let y = 0; y < dh; y++) {
    const s = vert.start[y];
    const n = vert.count[y];
    for (let x = 0; x < dw; x++) {
      let r = 0, g = 0, b = 0, a = 0;
      for (let k = 0; k < n; k++) {
        const weight = vert.w[y * vert.max + k];
        const i = ((s + k) * dw + x) * 4;
        r += tmp[i] * weight;
        g += tmp[i + 1] * weight;
        b += tmp[i + 2] * weight;
        a += tmp[i + 3] * weight;
      }
      const o = (y * dw + x) * 4;
      out.data[o] = clamp8(r);
      out.data[o + 1] = clamp8(g);
      out.data[o + 2] = clamp8(b);
      out.data[o + 3] = clamp8(a);
    }
  }
  return out;
}

function clamp8(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : Math.round(v);
}

/**
 * Downscale a canvas to a target long edge, halving on the GPU first and
 * finishing with Lanczos. Returns a new canvas.
 */
export function highQualityDownscale(
  source: AnyCanvas,
  targetW: number,
  targetH: number,
): AnyCanvas {
  let current: AnyCanvas = source;
  let w = (source as HTMLCanvasElement).width;
  let h = (source as HTMLCanvasElement).height;

  // Repeated exact halving is artefact-free and much cheaper than a single
  // huge Lanczos pass.
  while (w / 2 >= targetW && h / 2 >= targetH && w > 64 && h > 64) {
    const nw = Math.max(targetW, Math.floor(w / 2));
    const nh = Math.max(targetH, Math.floor(h / 2));
    const next = makeCanvas(nw, nh);
    const nctx = ctx2d(next);
    nctx.imageSmoothingEnabled = true;
    nctx.imageSmoothingQuality = "high";
    nctx.drawImage(current as CanvasImageSource, 0, 0, nw, nh);
    current = next;
    w = nw;
    h = nh;
  }

  if (w === targetW && h === targetH) return current;

  const ctx = ctx2d(current);
  const data = ctx.getImageData(0, 0, w, h);
  const resized = lanczosResize(data, targetW, targetH);
  const out = makeCanvas(targetW, targetH);
  ctx2d(out).putImageData(resized, 0, 0);
  return out;
}
